import { buildRelatedVariantPacks } from '@/experiences/sales-portal/domain/items/blocs/transformers/related-variant-packs-transformer';
import { getTodayDate } from '@/utils/date';
import {
  ItemDetailContentData,
  PurchaseHistoryEntry,
} from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import {
  mapPurchaseHistoryEntry,
  mapSaltInfo,
} from '@/experiences/sales-portal/domain/items/blocs/transformers/item-detail-content-transformer';
import { useGeneratedGetOrderEntryItemDetailQuery } from '@/experiences/sales-portal/domain/items/blocs/documents/item-detail-content.generated';
import { useMemo } from 'react';
import type { RelatedVariantPack } from '@/experiences/sales-portal/domain/items/blocs/transformers/related-variant-packs-transformer';

interface UseItemDetailLoaderParams {
  restaurantUUID: string;
  supplierUUID: string;
  timezone: string;
  variantPackUUID: string;
}

/** Endpoint-sourced item-detail data: SALT metadata, history, and related packs. */
export type ItemDetailLoaderData = Pick<
  ItemDetailContentData,
  | 'error'
  | 'loading'
  | 'purchaseHistory'
  | 'refetch'
  | 'relatedVariantPacks'
  | 'saltInfo'
>;

/**
 * Fetches the `getOrderEntryItemDetail` action for a single variant pack and
 * derives SALT metadata, purchase history, and related packs from the response.
 * The query is skipped until a variant pack is selected; purchase-history dates
 * are formatted, and the delivery date scoped, to `timezone`.
 *
 * @param params.restaurantUUID - Restaurant the item is viewed from.
 * @param params.supplierUUID - Supplier the item belongs to.
 * @param params.timezone - IANA timezone for the delivery date and history dates.
 * @param params.variantPackUUID - Variant pack to load; empty skips the fetch.
 * @returns The endpoint-derived item-detail data plus loading / error / refetch.
 */
export const useItemDetailLoader = ({
  restaurantUUID,
  supplierUUID,
  timezone,
  variantPackUUID,
}: UseItemDetailLoaderParams): ItemDetailLoaderData => {
  const { data, error, loading, refetch } =
    useGeneratedGetOrderEntryItemDetailQuery({
      notifyOnNetworkStatusChange: true,
      skip: !variantPackUUID,
      variables: {
        deliveryDate: getTodayDate(undefined, timezone),
        purchaseHistoryLimit: null,
        restaurantId: restaurantUUID,
        supplierId: supplierUUID,
        variantPackId: variantPackUUID,
      },
    });

  const detail = data?.getOrderEntryItemDetail;

  const saltInfo = useMemo(() => mapSaltInfo(detail?.salt_info), [detail]);

  const purchaseHistory = useMemo<PurchaseHistoryEntry[]>(
    () =>
      (detail?.purchase_history ?? []).map((entry) =>
        mapPurchaseHistoryEntry(entry, timezone),
      ),
    [detail, timezone],
  );

  const relatedVariantPacks = useMemo<RelatedVariantPack[]>(
    () => buildRelatedVariantPacks(detail?.related_variant_packs ?? []),
    [detail],
  );

  return {
    error: error ?? null,
    loading,
    purchaseHistory,
    refetch,
    relatedVariantPacks,
    saltInfo,
  };
};
